/**
 * Entry point. Boot, build, drive.
 *
 * The loop is deliberately small: sample the ground, step the selected
 * dynamics, move the camera and render. Candidate systems stay dynamically
 * imported. Everything that
 * makes the result specific to Hong Kong lives in `core/spec.ts` and the road
 * layer, not here.
 */
import { ACESFilmicToneMapping, PCFShadowMap, SRGBColorSpace, WebGLRenderer } from 'three';
import { createLoader } from './core/loader';
import { FixedStepClock } from './core/fixedStep';
import { parseFeatureSwitches } from './core/featureSwitches';
import {
  detectQuality,
  lowerQuality,
  PerfMonitor,
  pixelRatioCap,
  QUALITY_PROFILES,
  type Quality,
} from './core/perf';
import { unverifiedSpecs, VEHICLE_DERIVED } from './core/spec';
import { createCarState, speedKmh, stepCar } from './car/arcadeCar';
import { InputConditioner } from './car/inputConditioner';
import type { VehicleDynamics } from './car/vehicleDynamics';
import { createCarModel } from './car/carModel';
import { ChaseCamera } from './car/chaseCamera';
import { createFollowMemory, groundFollow } from './car/groundFollow';
import { nearestStationIndex, stationAtS } from './road/stations';
import { setupLighting } from './scene/lighting';
import { createMaterials } from './scene/materials';
import { setupSky } from './scene/sky';
import { createPostFx, type PostFxTier } from './scene/postfx';
import { assertAttributionVisible, mountAttribution } from './ui/attribution';
import { createControls } from './ui/dpad';
import { createHud } from './ui/hud';
import type { TexturedSceneStreamer } from './scene/streaming/sceneStreamer';
import type { RedLightPedestrianScenario } from './assessment/redLightPedestrian';
import type { TrainingSession } from './assessment/scoring';
import type { PedestrianActor } from './scene/pedestrianActor';
import { hashCanonicalJson } from './core/contentHash';
import { loadBuildIdentity } from './core/runtimeIdentity';
import type { SimulationProfile } from './core/productionContracts';
import type { BasicCockpit } from './car/cockpit';
import { FrameTimeDistribution } from './core/frameTimeDistribution';

/** Deterministic vehicle simulation, independent of display refresh rate. */
const FIXED_DT = 1 / 60;
const MAX_CATCH_UP_STEPS = 3;
/** Never accumulate more than this after a frame hitch or debugger pause. */
const MAX_FRAME_DT = FIXED_DT * MAX_CATCH_UP_STEPS;

function el<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`missing #${id}`);
  return found as T;
}

async function boot(): Promise<void> {
  const canvas = el<HTMLCanvasElement>('viewport');
  const bootEl = el('boot');
  const bootProgress = el('boot-progress');
  const bootFill = el('boot-fill');
  const bootMsg = el('boot-msg');
  const bootRetry = el<HTMLButtonElement>('boot-retry');

  const setBoot = (fraction: number, message: string) => {
    const percent = Math.round(Math.max(0, Math.min(1, fraction)) * 100);
    bootEl.dataset.state = 'loading';
    bootFill.style.width = `${percent}%`;
    bootProgress.setAttribute('aria-valuenow', String(percent));
    bootMsg.textContent = message;
  };
  const showFatal = (message: string, state: 'error' | 'context-lost' = 'error') => {
    bootEl.hidden = false;
    bootEl.classList.remove('is-done');
    bootEl.dataset.state = state;
    bootMsg.textContent = message;
    bootRetry.hidden = false;
  };
  const onRetry = () => location.reload();
  bootRetry.addEventListener('click', onRetry);

  mountAttribution(el('attribution'));

  const query = new URLSearchParams(location.search);
  const featureSwitches = parseFeatureSwitches(query);
  /** Deterministic visual-regression mode: render, but do not advance physics. */
  const freezeSimulation = query.get('freeze') === '1';
  const requestedQuality = featureSwitches.sceneQuality;
  const qualityLocked = requestedQuality !== null;
  let quality: Quality = requestedQuality ?? detectQuality();

  const renderer = new WebGLRenderer({
    canvas,
    antialias: quality === 'high',
    powerPreference: 'high-performance',
    // The scene is fully opaque; no alpha buffer, no stencil.
    alpha: false,
    stencil: false,
  });
  renderer.setPixelRatio(pixelRatioCap(quality));
  renderer.setSize(window.innerWidth, window.innerHeight, false);
  // Without these two lines every scene reads flat and washed out, no matter
  // what the materials do. See docs/RENDERING.md.
  // v0.2 P0: exposure 1.05 -> 1.18 default; query ?exposure=1.15/1.22/1.28 for A/B.
  const EXPOSURE_DEFAULT = 1.18;
  const exposureOverride = Number(query.get('exposure'));
  const exposure =
    Number.isFinite(exposureOverride) && exposureOverride >= 0.8 && exposureOverride <= 2
      ? exposureOverride
      : EXPOSURE_DEFAULT;
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = exposure;
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.shadowMap.type = PCFShadowMap;

  // A lost context is recoverable-ish; a silent white tab is not. Say so.
  canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    showFatal('WebGL 內容遺失，請重新載入頁面。', 'context-lost');
  });

  // Let the browser paint the branded loading state before synchronous
  // procedural texture generation starts.
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  setBoot(0.15, '產生路面材質…');
  const materials = createMaterials(renderer, quality);

  setBoot(0.35, '檢查資產…');
  const loader = createLoader(
    renderer,
    (f, m) => setBoot(0.35 + f * 0.35, m),
    { loadBuildings: true },
  );
  const assets = await loader.loadAll();

  setBoot(0.78, '建構道路…');
  const [{ ROUTE }, { buildWorld, describeReport, disposeWorld }] = await Promise.all([
    import('./road/route'),
    import('./scene/world'),
  ]);
  // Buildings are source data plus runtime geometry, so keep their extrusion
  // code out of the critical bundle when the optional GeoJSON is absent.
  const buildingFactory = assets.buildings
    ? (await import('./scene/buildings')).createBuildings
    : undefined;
  const world = await buildWorld(assets, materials, buildingFactory);
  setupSky(world.scene, query);
  const lighting = setupLighting(world.scene, renderer, quality, query);
  const requestedFx = query.get('postfx');
  const postFxLocked =
    requestedFx === 'off' || requestedFx === 'grade' || requestedFx === 'full';
  const initialFx: PostFxTier =
    postFxLocked
      ? requestedFx
      : QUALITY_PROFILES[quality].postFx;
  const postfx = createPostFx(renderer, initialFx);
  postfx.resize(window.innerWidth, window.innerHeight, renderer.getPixelRatio());

  const camera = new ChaseCamera(window.innerWidth / window.innerHeight);
  let cockpit: BasicCockpit | null = null;
  if (query.get('cockpit') === '1') {
    const { createBasicCockpit } = await import('./car/cockpit');
    cockpit = createBasicCockpit(materials);
    camera.camera.add(cockpit.root);
    world.scene.add(camera.camera);
  }
  const queryStation = query.has('station') ? Number(query.get('station')) : Number.NaN;
  const requestedStation =
    featureSwitches.dynamicsEngine === 'rapier' && !Number.isFinite(queryStation)
      ? 1050
      : queryStation;
  const fixedStart = Number.isFinite(requestedStation)
    ? stationAtS(world.stations, requestedStation)
    : null;
  const car = fixedStart
    ? createCarState(fixedStart.x, fixedStart.z, Math.atan2(fixedStart.tx, -fixedStart.tz))
    : createCarState(world.start.x, world.start.z, world.start.heading);
  let vehicleDynamics: VehicleDynamics | null = null;
  let activeDynamicsEngine: 'kinematic' | 'rapier' = 'kinematic';
  const candidateInput = new InputConditioner();
  if (featureSwitches.dynamicsEngine === 'rapier') {
    try {
      const { RapierDynamics } = await import('./car/rapierDynamics');
      vehicleDynamics = await RapierDynamics.create(
        { x: car.x, z: car.z, heading: car.heading },
        world.stations,
      );
      Object.assign(car, vehicleDynamics.snapshot());
      activeDynamicsEngine = 'rapier';
    } catch (error) {
      console.warn('[vehicle] Rapier candidate unavailable; using KinematicDynamics', error);
    }
  }
  let sceneStreamer: TexturedSceneStreamer | null = null;
  let activeSceneMode: 'procedural' | 'textured' = 'procedural';
  if (featureSwitches.sceneMode === 'textured') {
    try {
      const { TexturedSceneStreamer: Streamer } = await import(
        './scene/streaming/sceneStreamer'
      );
      sceneStreamer = await Streamer.create(world.scene, renderer, quality);
      await sceneStreamer.update(fixedStart?.s ?? 0, 0);
      activeSceneMode = 'textured';
    } catch (error) {
      console.warn('[stream] textured candidate unavailable; using procedural fallback', error);
    }
  }
  const followMemory = createFollowMemory();
  const carModel = createCarModel(materials);
  carModel.setContactShadow(!QUALITY_PROFILES[quality].shadows);
  world.scene.add(carModel.root);

  const controls = createControls(el('controls'));
  controls.setCameraMode(camera.mode);
  const hud = createHud(document.body, quality);
  let trainingSession: TrainingSession | null = null;
  let trainingScenario: RedLightPedestrianScenario | null = null;
  let pedestrianActor: PedestrianActor | null = null;
  let assessmentCertified = false;
  if (featureSwitches.assessmentMode === 'scoring-v1') {
    const [{ TrainingSession }, { RedLightPedestrianScenario, RED_LIGHT_PEDESTRIAN_SCENARIO }, { createPedestrianActor }, { loadCertificationRegistry }, { profileMatchesCertification }] =
      await Promise.all([
        import('./assessment/scoring'),
        import('./assessment/redLightPedestrian'),
        import('./scene/pedestrianActor'),
        import('./core/certificationRegistry'),
        import('./core/receiptValidation'),
      ]);
    const identity = await loadBuildIdentity();
    const profile: SimulationProfile = {
      ...identity,
      sceneMode: activeSceneMode,
      sceneQuality: quality,
      sceneManifestHash:
        sceneStreamer?.manifest.manifestHash ??
        (await hashCanonicalJson({ scene: 'procedural', route: ROUTE.name, schema: 1 })),
      dynamicsEngine: activeDynamicsEngine,
      dynamicsEngineVersion: activeDynamicsEngine === 'rapier' ? '0.20.0-spike.1' : 'kinematic/1',
      dynamicsEngineHash: await hashCanonicalJson({ engine: activeDynamicsEngine, schema: 1 }),
      vehicleConfigHash: await hashCanonicalJson({ vehicle: VEHICLE_DERIVED, schema: 1 }),
      assessmentMode: 'scoring-v1',
      scoringPolicyHash: await hashCanonicalJson({ policy: 'scoring-v1', scenario: RED_LIGHT_PEDESTRIAN_SCENARIO }),
    };
    try {
      const registry = await loadCertificationRegistry();
      for (const receipt of registry) {
        const candidate = {
          ...profile,
          certificationId: receipt.certificationId,
          certificationHash: receipt.certificationHash,
        };
        if (profileMatchesCertification(candidate, receipt)) {
          Object.assign(profile, candidate);
          assessmentCertified = true;
          break;
        }
      }
    } catch (error) {
      console.warn('[assessment] certification registry unavailable; Experimental only', error);
    }
    trainingSession = new TrainingSession(
      profile,
      RED_LIGHT_PEDESTRIAN_SCENARIO.version,
      await hashCanonicalJson(RED_LIGHT_PEDESTRIAN_SCENARIO),
      query.get('seed') ?? 'normal-v1',
      assessmentCertified,
    );
    trainingScenario = new RedLightPedestrianScenario(world.stations, trainingSession);
    pedestrianActor = createPedestrianActor();
    world.scene.add(pedestrianActor.root);
    hud.setAssessmentStatus(assessmentCertified ? 'Training assessment' : 'Experimental driving');
  }

  // Snap the vehicle onto the road and initialise camera/shadow state before
  // compiling. This prevents both an underground first frame and shader work
  // landing in the first interactive frame.
  let follow = groundFollow(car, world.sampler, followMemory, FIXED_DT);
  carModel.sync(car);
  camera.update(car, FIXED_DT);
  lighting.follow(car.x, car.z);
  setBoot(0.92, '最佳化著色器…');
  try {
    await renderer.compileAsync(world.scene, camera.camera);
  } catch (error) {
    // Parallel shader compilation is an optimisation, never a boot gate.
    console.warn('[render] shader precompile unavailable:', error);
  }
  // Start the rolling clock after boot/precompile so loading work cannot be
  // mistaken for four seconds of poor in-game frame rate.
  const perf = new PerfMonitor(renderer);
  const frameTimes = new FrameTimeDistribution();
  const benchmarkStartedAt = performance.now();

  // Boot report: the acceptance numbers, printed where they cannot be ignored.
  const report = describeReport(world.report);
  console.info(['HK Drive Sim — ' + ROUTE.name, ...report].join('\n  '));
  console.info('[v1] requested simulation switches', featureSwitches, {
    activeSceneMode,
    activeDynamicsEngine,
    activeAssessmentMode: featureSwitches.assessmentMode,
  });
  if (
    featureSwitches.sceneMode !== 'procedural' ||
    featureSwitches.dynamicsEngine !== 'kinematic' ||
    featureSwitches.assessmentMode !== 'practice'
  ) {
    console.info(
      '[v1] candidate profile is Experimental; only certified combinations may emit Training Pass/Fail',
    );
  }
  const pending = unverifiedSpecs();
  if (pending.length > 0) {
    console.warn(
      `[spec] ${pending.length} dimension(s) are still placeholders, not official values:\n  ` +
        pending.join('\n  ') +
        '\n  Fill these from the TPDM / HyD PDFs in docs/specs before calling the ' +
        'driving layer "built to spec".',
    );
  }

  const applyResize = () => {
    renderer.setPixelRatio(pixelRatioCap(quality));
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    postfx.resize(window.innerWidth, window.innerHeight, renderer.getPixelRatio());
    camera.setAspect(window.innerWidth / window.innerHeight);
  };
  let resizeFrame = 0;
  const onResize = () => {
    if (resizeFrame) return;
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      applyResize();
    });
  };
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize, { passive: true });
  window.visualViewport?.addEventListener('resize', onResize, { passive: true });

  // ---- main loop ----
  let last = performance.now();
  const simulationClock = new FixedStepClock(FIXED_DT, MAX_CATCH_UP_STEPS);
  let streetIndex = -1;
  let stationIndex = fixedStart ? nearestStationIndex(world.stations, fixedStart.x, fixedStart.z) : 0;
  const initialProfile = QUALITY_PROFILES[quality];
  let chunksVisible = world.updateVisible(
    fixedStart?.s ?? 0,
    initialProfile.roadRadiusM,
    initialProfile.propRadiusM,
  );
  const perfSweep = query.get('perf') === '1';
  const sweepStages = ['baseline', 'shadows off', 'postfx off', 'dpr 1.0'] as const;
  const sweepResults: { stage: string; fps: number; cpuMs: number; gpuMs: number | null }[] = [];
  let sweepIndex = 0;
  let sweepStarted = performance.now();
  const baselineShadow = renderer.shadowMap.enabled;
  const baselineDpr = renderer.getPixelRatio();
  const applySweepStage = (stage: (typeof sweepStages)[number]) => {
    renderer.shadowMap.enabled = baselineShadow;
    renderer.setPixelRatio(baselineDpr);
    postfx.setTier(initialFx);
    if (stage === 'shadows off') renderer.shadowMap.enabled = false;
    if (stage === 'postfx off') postfx.setTier('off');
    if (stage === 'dpr 1.0') renderer.setPixelRatio(1);
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    postfx.resize(window.innerWidth, window.innerHeight, renderer.getPixelRatio());
  };
  if (perfSweep) applySweepStage(sweepStages[0]);
  /** Animation-frame handle; the loop runs only while `running`. */
  let rafId = 0;
  let running = false;
  let disposed = false;

  const frame = (now: number) => {
    if (!running) return;
    const cpuStart = performance.now();
    const dt = Math.min(MAX_FRAME_DT, Math.max(0, (now - last) / 1000));
    last = now;
    // Contract benchmark window: first 30 s warmup, then up to 120 s samples.
    const benchmarkElapsed = now - benchmarkStartedAt;
    if (benchmarkElapsed >= 30_000 && benchmarkElapsed < 150_000) {
      frameTimes.add(dt * 1_000);
    }

    if (controls.consumeCameraToggle()) {
      camera.toggle();
      controls.setCameraMode(camera.mode);
      hud.setCameraMode(camera.mode);
      cockpit?.setVisible(camera.mode === 'first');
    }

    const input = {
      throttle: controls.throttle,
      brake: controls.brake,
      steer: controls.steer,
    };
    if (!freezeSimulation) {
      simulationClock.advance(dt, (stepSeconds) => {
        // Sample first so gravity and attitude use this step's road, not a
        // display-frame-old value. The fixed step makes dwell and steering
        // deterministic on 30, 60, 90 and 120 Hz displays.
        if (vehicleDynamics) {
          const conditioned = candidateInput.sample(input, stepSeconds);
          vehicleDynamics.step(conditioned, stepSeconds);
          Object.assign(car, vehicleDynamics.snapshot());
          // Ground sampling remains diagnostic only for Rapier: its wheel rays,
          // suspension and road collider own the candidate chassis state.
          const probe = { ...car };
          follow = groundFollow(probe, world.sampler, followMemory, stepSeconds);
        } else {
          follow = groundFollow(car, world.sampler, followMemory, stepSeconds);
          stepCar(car, input, stepSeconds, follow.grade);
        }
        if (trainingScenario && trainingSession) {
          const assessmentStationIndex = nearestStationIndex(
            world.stations,
            car.x,
            car.z,
            stationIndex,
          );
          const assessmentStation = world.stations[assessmentStationIndex]?.s ?? 0;
          pedestrianActor?.sync(trainingScenario.update(car, assessmentStation, stepSeconds));
          const result = trainingSession.result();
          hud.setAssessmentStatus(
            `${assessmentCertified ? result.passState : 'Experimental'} · ${result.violations.length} error${result.violations.length === 1 ? '' : 's'}`,
          );
        }
      });
    }

    carModel.sync(car);
    cockpit?.sync(car);
    camera.update(car, dt);
    carModel.setVisible(camera.mode === 'chase');
    lighting.follow(car.x, car.z);

    const nextStationIndex = nearestStationIndex(world.stations, car.x, car.z, stationIndex);
    const stationChanged = nextStationIndex !== stationIndex;
    stationIndex = nextStationIndex;
    const station = world.stations[stationIndex];
    if (station && stationChanged) {
      const profile = QUALITY_PROFILES[quality];
      chunksVisible = world.updateVisible(station.s, profile.roadRadiusM, profile.propRadiusM);
      void sceneStreamer?.update(station.s, car.speed);
    }

    perf.beginFrame();
    perf.beginGpu();
    postfx.render(world.scene, camera.camera);
    sceneStreamer?.markFrameRendered();
    perf.endGpu();

    const stats = perf.tick(performance.now() - cpuStart);
    hud.setSpeed(speedKmh(car));
    hud.setOffRoad(car.offRoad);

    const nearest = station?.legIndex ?? 0;
    if (nearest !== streetIndex) {
      streetIndex = nearest;
      hud.setStreet(ROUTE.legs[nearest]?.street ?? '');
    }

    if (hud.diagnosticsVisible) {
      hud.setDiagnostics([
        `fps ${stats.fps}   cpu ${stats.cpuMs.toFixed(1)} ms   gpu ${stats.gpuMs?.toFixed(1) ?? 'n/a'} ms`,
        `draw ${stats.drawCalls}   tri ${stats.triangles.toLocaleString()}   chunks ${chunksVisible}`,
        `quality ${quality}   dpr ${renderer.getPixelRatio().toFixed(2)}   aniso ${materials.anisotropy}`,
        `exposure ${exposure.toFixed(2)}   sun ${lighting.sun.intensity.toFixed(1)}   fogNear ${(world.scene.fog as import('three').Fog).near}`,
        `shadow ${lighting.sun.shadow.mapSize.x} px   radius ${lighting.radiusM} m`,
        `postfx ${postfx.tier}`,
        `scene ${activeSceneMode}` +
          (sceneStreamer
            ? `  textured ${sceneStreamer.diagnostics().activeChunks.join(',') || 'prefetch'}  gpu~${sceneStreamer.diagnostics().calculatedGpuMB}MB`
            : ''),
        `dynamics ${activeDynamicsEngine}${activeDynamicsEngine === 'rapier' ? '  EXPERIMENTAL' : ''}`,
        `speed ${speedKmh(car).toFixed(1)} km/h   grade ${(follow.grade * 100).toFixed(1)}%` +
          (Math.abs(follow.rawGrade - follow.grade) > 1e-6
            ? `  (raw ${(follow.rawGrade * 100).toFixed(1)}% CLAMPED)`
            : ''),
        `wheels on road ${follow.wheelsOnRoad}/4${car.offRoad ? '  OFF ROAD' : ''}`,
        `steer ${((car.steerAngle * 180) / Math.PI).toFixed(1)}deg  ` +
          `max ${((VEHICLE_DERIVED.MAX_STEER_RAD * 180) / Math.PI).toFixed(1)}deg`,
        `pos x ${car.x.toFixed(1)}  y ${car.y.toFixed(2)}  z ${car.z.toFixed(1)}`,
        '',
        ...report,
        ...perf.overBudget().map((s) => `OVER BUDGET: ${s}`),
      ]);
    }

    if (perfSweep && sweepIndex < sweepStages.length && now - sweepStarted >= 1200) {
      sweepResults.push({
        stage: sweepStages[sweepIndex]!,
        fps: stats.fps,
        cpuMs: Number(stats.cpuMs.toFixed(2)),
        gpuMs: stats.gpuMs === null ? null : Number(stats.gpuMs.toFixed(2)),
      });
      sweepIndex++;
      sweepStarted = now;
      const next = sweepStages[sweepIndex];
      if (next) applySweepStage(next);
      else {
        applySweepStage('baseline');
        const baseline = sweepResults[0];
        console.table(
          sweepResults.map((result) => ({
            ...result,
            fpsDelta: baseline ? result.fps - baseline.fps : 0,
            gpuDeltaMs:
              baseline?.gpuMs !== null && result.gpuMs !== null && baseline?.gpuMs !== undefined
                ? Number((result.gpuMs - baseline.gpuMs).toFixed(2))
                : null,
          })),
        );
      }
    }

    // One-way downgrade if the device cannot hold the target frame rate.
    if (!perfSweep && !qualityLocked && perf.shouldDowngrade && quality !== 'low') {
      quality = lowerQuality(quality);
      const profile = QUALITY_PROFILES[quality];
      lighting.setQuality(quality);
      renderer.setPixelRatio(pixelRatioCap(quality));
      renderer.setSize(window.innerWidth, window.innerHeight, false);
      materials.setQuality(quality);
      carModel.setContactShadow(!profile.shadows);
      if (!postFxLocked) postfx.setTier(profile.postFx);
      postfx.resize(window.innerWidth, window.innerHeight, renderer.getPixelRatio());
      if (station) {
        chunksVisible = world.updateVisible(station.s, profile.roadRadiusM, profile.propRadiusM);
      }
      hud.setQuality(quality);
      perf.acknowledgeDowngrade();
      console.info(`[perf] dropped to ${quality} quality to hold the frame-rate target`);
    }

    rafId = requestAnimationFrame(frame);
  };

  const startLoop = () => {
    if (disposed || running) return;
    running = true;
    last = performance.now();
    simulationClock.reset();
    candidateInput.reset();
    rafId = requestAnimationFrame(frame);
  };
  const stopLoop = () => {
    running = false;
    cancelAnimationFrame(rafId);
  };

  // Pause when the tab hides; resume without a giant dt when it returns.
  const onVisibility = () => {
    if (disposed) return;
    if (document.hidden) stopLoop();
    else startLoop();
  };
  document.addEventListener('visibilitychange', onVisibility);

  setBoot(1, '準備就緒');
  bootEl.dataset.state = 'ready';
  bootEl.classList.add('is-done');
  const bootHideTimer = window.setTimeout(() => {
    bootEl.hidden = true;
    assertAttributionVisible(el('attribution'));
  }, 360);

  startLoop();

  // Production device benchmarks opt in explicitly. This exposes local
  // benchmark controls only; it does not transmit telemetry or change defaults.
  if (import.meta.env.DEV || query.get('diagnostics') === '1') {
    window.__HK_DRIVE_DIAGNOSTICS__ = {
      stream: () => sceneStreamer?.diagnostics() ?? null,
      streamPosition: async (stationS, speedMs) => {
        await sceneStreamer?.update(stationS, speedMs);
      },
      session: () => trainingSession?.result() ?? null,
      frames: () => frameTimes.report(),
    };
  }

  // Tidy up on navigation away - matters for the dev server's hot reloads.
  // But NOT on bfcache entry: `pagehide` fires with `persisted` true when iOS
  // Safari snapshots the page, and disposing there leaves a white canvas on
  // resume. That is worse than the leak. The loop pauses via visibilitychange.
  window.addEventListener('pagehide', (e) => {
    if (e.persisted) return;
    disposed = true;
    stopLoop();
    window.clearTimeout(bootHideTimer);
    cancelAnimationFrame(resizeFrame);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onResize);
    window.visualViewport?.removeEventListener('resize', onResize);
    bootRetry.removeEventListener('click', onRetry);
    controls.dispose();
    vehicleDynamics?.dispose();
    if (trainingSession) {
      sessionStorage.setItem('hk-drive-sim:last-session', JSON.stringify(trainingSession.result()));
    }
    pedestrianActor?.dispose();
    cockpit?.dispose();
    delete window.__HK_DRIVE_DIAGNOSTICS__;
    sceneStreamer?.dispose();
    hud.dispose();
    loader.dispose();
    perf.dispose();
    postfx.dispose();
    disposeWorld(world);
    materials.dispose();
    renderer.dispose();
  });
}

boot().catch((err: unknown) => {
  console.error('[boot] failed', err);
  const boot = document.getElementById('boot');
  const msg = document.getElementById('boot-msg');
  const retry = document.getElementById('boot-retry');
  if (boot) {
    boot.hidden = false;
    boot.classList.remove('is-done');
    boot.dataset.state = 'error';
  }
  if (msg) {
    // textContent, not innerHTML: an error message must never be able to
    // inject markup into the page.
    msg.textContent = '載入失敗，請重新載入頁面。';
  }
  if (retry instanceof HTMLButtonElement) retry.hidden = false;
});
